import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { onFrame, sendFrame } from '../../lib/socket';

interface Screen {
  id: string;
  label: string;
  route: string;
  kind: 'screen' | 'scene';
}

interface Issue {
  id: string;
  title: string;
  severity: string;
  screenRef: string | null;
  status: string;
}

interface Pin {
  x: number;
  y: number;
  text: string;
}

/**
 * Testing mode (spec 5b.3): every screen at once with dummy data, pick one to review closely, then
 * mark it up and send the whole lot to the AI as one request.
 *
 * FABLE-HANDOFF (#16): the pen tool draws onto the overlay canvas below — capture it together with a
 * snapshot of the frame (canvas `toDataURL` / DOM rasterisation) and post as `imageDataUrl`. Pins are
 * already sent as structured data; keep them that way so no typed note depends on OCR.
 */
interface CollabState {
  active: boolean;
  initiatorId: string;
  proposed: boolean;
  acks: string[];
}

export function TestingMode({ projectId, onSent }: { projectId: string; onSent: () => void }) {
  const { user } = useAuth();
  const [screens, setScreens] = useState<Screen[] | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [html, setHtml] = useState('');
  const [noun, setNoun] = useState<'screen' | 'scene'>('screen');
  const [active, setActive] = useState<Screen | null>(null);
  const [pins, setPins] = useState<Pin[]>([]);
  const [note, setNote] = useState('');
  const [drawing, setDrawing] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [collab, setCollab] = useState<CollabState | null>(null);
  const collabRef = useRef<CollabState | null>(null);
  const pinsRef = useRef<Pin[]>([]);
  useEffect(() => {
    collabRef.current = collab;
  }, [collab]);
  useEffect(() => {
    pinsRef.current = pins;
  }, [pins]);

  /* Collaborative review (#27 / 5b.3): live pins + the proceed/force-submit handshake ride the
     project socket; the INITIATOR's device performs the actual submission (job leadership). */
  useEffect(() => {
    return onFrame(frame => {
      const me = user?.id;
      switch (frame.t) {
        case 'review-start':
          setCollab({ active: true, initiatorId: String(frame.initiatorId), proposed: false, acks: [] });
          setStatus(String(frame.byName ?? 'Someone') + ' started a group review');
          break;
        case 'review-state':
          setCollab({
            active: true,
            initiatorId: String(frame.initiatorId),
            proposed: frame.state === 'proposed',
            acks: (frame.acks as string[]) ?? [],
          });
          if (Array.isArray(frame.pins)) {
            setPins((frame.pins as Array<{ x: number; y: number; text: string }>).map(p => ({ x: p.x, y: p.y, text: p.text })));
          }
          break;
        case 'pin':
          if (frame.by !== me && typeof frame.x === 'number' && typeof frame.y === 'number') {
            setPins(prev => [...prev, { x: Number(frame.x), y: Number(frame.y), text: String(frame.text ?? '') }]);
          }
          break;
        case 'stroke':
          if (frame.by !== me && typeof frame.xf === 'number' && typeof frame.yf === 'number') {
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext('2d');
            if (canvas && ctx) {
              ctx.fillStyle = '#3b82f6'; // collaborators draw in blue, you in red
              ctx.beginPath();
              ctx.arc(Number(frame.xf) * canvas.width, Number(frame.yf) * canvas.height, 2.5, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          break;
        case 'review-propose':
          setCollab(prev => (prev ? { ...prev, proposed: true } : prev));
          setStatus('Ready to send — everyone taps Proceed');
          break;
        case 'review-ack':
          setCollab(prev => (prev ? { ...prev, acks: (frame.acks as string[]) ?? prev.acks } : prev));
          break;
        case 'review-submit': {
          const current = collabRef.current;
          setCollab(null);
          setStatus(frame.forced ? 'Sent (the review leader went ahead)' : 'Everyone agreed — sent');
          // job leadership: only the initiator's device performs the submission
          if (current && current.initiatorId === me) {
            const merged = Array.isArray(frame.pins)
              ? (frame.pins as Array<{ x: number; y: number; text: string }>).map(p => ({ x: p.x, y: p.y, text: p.text }))
              : pinsRef.current;
            void submitFeedback(merged);
          }
          break;
        }
        case 'review-cancel':
          setCollab(null);
          setStatus('Group review ended');
          break;
        default:
          break;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{
        screens: Screen[];
        bundle: { html: string };
        noun: 'screen' | 'scene';
        issues: Issue[];
      }>(`/box/projects/${projectId}/testing/screens`);
      setScreens(data.screens);
      setHtml(data.bundle.html);
      setNoun(data.noun);
      setIssues(data.issues);
    } catch {
      setStatus('Could not load your screens');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  /* ------------------------------- pen tool on the overlay ---------------------------------- */

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    if (!canvas || e.buttons !== 1) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ef4444';
    ctx.beginPath();
    ctx.arc(e.clientX - rect.left, e.clientY - rect.top, 2.5, 0, Math.PI * 2);
    ctx.fill();
    if (collabRef.current?.active) {
      sendFrame({ t: 'stroke', xf: (e.clientX - rect.left) / rect.width, yf: (e.clientY - rect.top) / rect.height });
    }
  };

  const clearDoodles = () => {
    const canvas = canvasRef.current;
    canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  };

  const addPin = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!pinning) return;
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const text = window.prompt('What should change here?');
    if (!text?.trim()) return;
    const pin = { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height, text: text.trim() };
    setPins(prev => [...prev, pin]);
    if (collabRef.current?.active) sendFrame({ t: 'pin', xf: pin.x, yf: pin.y, text: pin.text });
  };

  /**
   * Snapshot capture (#16): ask the app (via its injected bridge) to rasterise itself, then bake the
   * learner's pen strokes on top. Best-effort — pins are structured data and carry the meaning even
   * when the image fails (opaque-origin iframes cannot be captured from outside).
   */
  const captureAnnotatedSnapshot = (): Promise<string | null> =>
    new Promise(resolve => {
      const frame = stageRef.current?.querySelector('iframe');
      if (!frame?.contentWindow) return resolve(null);
      const id = `snap-${Date.now().toString(36)}`;
      const timer = window.setTimeout(() => {
        window.removeEventListener('message', onMessage);
        resolve(null);
      }, 3500);
      const onMessage = (event: MessageEvent) => {
        const data = event.data as { v?: string; type?: string; id?: string; payload?: { dataUrl?: string | null } };
        if (data?.v !== 'cbx:v1' || data.type !== 'snapshot-result' || data.id !== id) return;
        window.clearTimeout(timer);
        window.removeEventListener('message', onMessage);
        const base = data.payload?.dataUrl;
        if (!base) return resolve(null);
        // merge the pen overlay onto the app snapshot
        const img = new Image();
        img.onload = () => {
          try {
            const out = document.createElement('canvas');
            out.width = img.width;
            out.height = img.height;
            const ctx = out.getContext('2d')!;
            ctx.drawImage(img, 0, 0);
            const overlay = canvasRef.current;
            if (overlay) ctx.drawImage(overlay, 0, 0, out.width, out.height);
            resolve(out.toDataURL('image/png'));
          } catch {
            resolve(base);
          }
        };
        img.onerror = () => resolve(base);
        img.src = base;
      };
      window.addEventListener('message', onMessage);
      frame.contentWindow.postMessage({ v: 'cbx:v1', type: 'snapshot-request', id }, '*');
    });

  const submitFeedback = async (whichPins: Pin[]) => {
    setStatus('Sending');
    try {
      const imageDataUrl = await captureAnnotatedSnapshot();
      await api.post(`/box/projects/${projectId}/testing/feedback`, {
        screenRef: active?.id,
        pins: whichPins,
        note: note.trim() || undefined,
        imageDataUrl: imageDataUrl ?? undefined,
      });
      setPins([]);
      setNote('');
      clearDoodles();
      setStatus('Sent — Sparky will work through your notes');
      onSent();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not send that feedback');
    }
  };

  const send = async () => {
    if (pins.length === 0 && !note.trim()) {
      setStatus('Add a note or a pin first');
      return;
    }
    if (collab?.active) {
      // group review: the initiator proposes; everyone confirms (or the initiator forces)
      if (collab.initiatorId === user?.id) sendFrame({ t: 'review-propose' });
      else setStatus('Only the person who started the review can send it');
      return;
    }
    await submitFeedback(pins);
  };

  const startCollab = () => {
    sendFrame({ t: 'review-start' });
    setCollab({ active: true, initiatorId: user?.id ?? '', proposed: false, acks: [] });
    setStatus('Group review started — everyone on this project can doodle and pin');
  };

  if (!screens) return <div className="m-4 h-40 animate-pulse rounded-lg bg-page" />;

  /* ----------------------------------- bird's-eye map ---------------------------------------- */

  if (!active) {
    return (
      <div className="space-y-4 overflow-auto p-4">
        <div>
          <h2 className="text-sm font-semibold">Every {noun} at a glance</h2>
          <p className="text-sm text-muted">
            Pick one to look at closely and leave notes on what should change.
          </p>
        </div>

        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {screens.map(screen => {
            const screenIssues = issues.filter(i => i.screenRef === screen.id);
            return (
              <li key={screen.id}>
                <button
                  type="button"
                  onClick={() => setActive(screen)}
                  className="w-full overflow-hidden rounded-xl border border-line text-left transition-colors hover:border-accent"
                >
                  <div className="relative h-40 bg-page">
                    <iframe
                      title={screen.label}
                      sandbox="allow-scripts"
                      srcDoc={html}
                      // scaled-down, non-interactive thumbnail of the real app
                      className="pointer-events-none h-[400px] w-[320%] origin-top-left scale-[0.31] border-0 bg-white"
                    />
                    {screenIssues.length > 0 && (
                      <span className="absolute right-2 top-2 rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-medium text-accent-ink">
                        {screenIssues.length}
                      </span>
                    )}
                  </div>
                  <div className="border-t border-line px-3 py-2">
                    <p className="truncate text-sm font-medium">{screen.label}</p>
                    <p className="truncate text-xs text-muted">{screen.route}</p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>

        {status && <p className="text-sm text-muted">{status}</p>}
      </div>
    );
  }

  /* -------------------------------- focused review + markup ---------------------------------- */

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <button
          type="button"
          onClick={() => setActive(null)}
          className="rounded-lg border border-line px-2.5 py-1 text-xs transition-colors hover:bg-page"
        >
          All {noun}s
        </button>
        <span className="text-sm font-medium">{active.label}</span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              setDrawing(d => !d);
              setPinning(false);
            }}
            className={[
              'rounded-lg px-2.5 py-1 text-xs font-medium transition-colors',
              drawing ? 'bg-accent text-accent-ink' : 'border border-line hover:bg-page',
            ].join(' ')}
          >
            Pen
          </button>
          <button
            type="button"
            onClick={() => {
              setPinning(p => !p);
              setDrawing(false);
            }}
            className={[
              'rounded-lg px-2.5 py-1 text-xs font-medium transition-colors',
              pinning ? 'bg-accent text-accent-ink' : 'border border-line hover:bg-page',
            ].join(' ')}
          >
            Add note
          </button>
          <button
            type="button"
            onClick={clearDoodles}
            className="rounded-lg border border-line px-2.5 py-1 text-xs transition-colors hover:bg-page"
          >
            Clear pen
          </button>
          {!collab?.active ? (
            <button
              type="button"
              onClick={startCollab}
              className="rounded-lg border border-line px-2.5 py-1 text-xs font-medium transition-colors hover:bg-page"
            >
              Review together
            </button>
          ) : collab.proposed && collab.initiatorId !== user?.id ? (
            <button
              type="button"
              onClick={() => sendFrame({ t: 'review-ack' })}
              className="rounded-lg bg-accent px-2.5 py-1 text-xs font-medium text-accent-ink"
            >
              Proceed ({collab.acks.length} ready)
            </button>
          ) : collab.initiatorId === user?.id && collab.proposed ? (
            <button
              type="button"
              onClick={() => sendFrame({ t: 'review-force' })}
              className="rounded-lg border border-line px-2.5 py-1 text-xs font-medium transition-colors hover:bg-page"
              title="Send without waiting for everyone"
            >
              Force send ({collab.acks.length} ready)
            </button>
          ) : (
            <span className="text-[11px] text-muted">group review live</span>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto bg-page p-4">
        <div
          ref={stageRef}
          onClick={addPin}
          className="relative mx-auto w-full max-w-3xl"
          style={{ cursor: pinning ? 'crosshair' : 'default' }}
        >
          <iframe
            title={`${active.label} preview`}
            // TEMP QUICK PATCH (camera/peripherals) — same deferred security debt as PreviewPane.tsx.
            sandbox="allow-scripts allow-same-origin"
            allow="camera; microphone; display-capture; geolocation"
            srcDoc={html}
            className="h-[460px] w-full rounded-lg border border-line bg-white"
          />
          <canvas
            ref={canvasRef}
            width={900}
            height={460}
            onMouseMove={draw}
            className="absolute inset-0 h-full w-full rounded-lg"
            style={{ pointerEvents: drawing ? 'auto' : 'none', cursor: drawing ? 'crosshair' : 'default' }}
          />
          {pins.map((pin, i) => (
            <span
              key={i}
              title={pin.text}
              className="absolute flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-accent text-[10px] font-medium text-white"
              style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%` }}
            >
              {i + 1}
            </span>
          ))}
        </div>

        {pins.length > 0 && (
          <ol className="mx-auto mt-3 max-w-3xl space-y-1">
            {pins.map((pin, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent text-[10px] text-white">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1">{pin.text}</span>
                <button
                  type="button"
                  onClick={() => setPins(prev => prev.filter((_, j) => j !== i))}
                  className="shrink-0 text-xs text-muted hover:text-ink"
                >
                  Remove
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="space-y-2 border-t border-line p-3">
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={2}
          placeholder="Anything else about this screen as a whole?"
          className="w-full resize-y rounded-lg border border-line bg-surface p-2 text-sm outline-none ring-1 ring-transparent transition focus:border-ink/50 focus:ring-ink/20"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-xs text-muted">
            {status ?? `${pins.length} note${pins.length === 1 ? '' : 's'} pinned — sent as one request`}
          </span>
          <button
            type="button"
            onClick={() => void send()}
            className="shrink-0 rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-accent-ink transition-opacity hover:opacity-90"
          >
            Send to Sparky
          </button>
        </div>
      </div>
    </div>
  );
}
